// Pure helpers behind the seller product / bundle tables on /seller/products and
// /seller/bundles (seller UI revamp, docs/specs/2026-10-03-seller-ui-revamp-design.md).
//
// WHY A MODULE AND NOT COMPUTED PROPERTIES. This repo's jest cannot mount a
// .vue file (see tests/unit/skuWmsOptionsSource.spec.js), so every rule the
// owner can be wrong about lives here and the two pages only render it.
// Behaviour is specified in tests/unit/sellerProductRow.spec.js.
//
// --------------------------------------------------------------------------
// THE THREE RULES THIS FILE OWNS
//
// 1. สถานะการขาย (spec §4). `พร้อมขาย` iff the thing is switched ON *and* has
//    sellable stock. The stock half is SKIPPED when `sellable_qty` is absent —
//    an API older than the BE half of this spec sends no stock at all on the
//    list row, and treating "absent" as 0 would paint the whole catalogue
//    yellow (spec §1, edge case 4). Bundles have no stock half at all: they
//    are a recipe over other people's SKUs, so `status === 1` is the whole
//    answer.
//
// 2. The ⚡ bolt (spec §5). `has_active_flash` is what the LIST row carries;
//    the per-product detail does not compute it, so the same question asked of
//    a detail object is answered from the SKUs' own flash window. Either
//    source saying yes is yes. The price test is `> 0` and not `!= null`
//    (owner's rule, BE-4): a 0 flash price is not a flash sale.
//
// 3. /app-catalog → /seller (spec §2). One function so the redirect route, the
//    sidebar and the pages cannot disagree about the rewrite.
// --------------------------------------------------------------------------

import { pickImageUrl, cdnImageUrl } from './cdnImage';

const APP_CATALOG_PREFIX = '/app-catalog';
const SELLER_PREFIX = '/seller';

/** Storefront origin used when the build does not name one. */
const DEFAULT_STOREFRONT_ORIGIN = 'https://www.madamefin.com';

/**
 * storefrontOrigin() — where "ดูหน้าร้าน" points (www serves /p/{id}).
 *
 * Read from the build env, so bo-stg opens the STAGING storefront instead of
 * sending staff to production www. Resolved per call and not once at import, so
 * it is testable; the fallback is prod because that is what every current
 * build_*.sh produces. A trailing slash in the variable must not become `//p/`.
 */
export function storefrontOrigin() {
  const fromEnv =
    typeof process !== 'undefined' && process.env ? process.env.VUE_APP_STOREFRONT_ORIGIN : '';
  const origin = String(fromEnv || '').trim() || DEFAULT_STOREFRONT_ORIGIN;
  return origin.replace(/\/+$/, '');
}

/**
 * Truthy test for the API's many spellings of a flag: 1 / "1" / true all mean
 * on; 0 / "0" / false / "" / null / undefined all mean off.
 */
function isOn(v) {
  if (v === true) return true;
  if (v === false || v === null || v === undefined || v === '') return false;
  return Number(v) === 1;
}

/** Number or null — null meaning "the API did not say", NOT "zero". */
function numOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
}

function toTime(v) {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) {
    const t = v.getTime();
    return isFinite(t) ? t : null;
  }
  const ms = typeof v === 'number' ? v : Date.parse(String(v));
  return isFinite(ms) ? ms : null;
}

function firstNonEmpty() {
  for (let i = 0; i < arguments.length; i++) {
    const v = arguments[i];
    if (v !== null && v !== undefined && String(v).trim() !== '') return v;
  }
  return '';
}

/**
 * saleStatusOf(product) → { key, type, labelKey, hintKey }
 *
 *   key      'ready' | 'not_ready'
 *   type     el-tag type: 'success' for ready, 'warning' for not ready
 *   labelKey i18n key of the badge word
 *   hintKey  i18n key of the tooltip, '' when ready (nothing to explain)
 *
 * Reads `app_enable` for a product and `status` for a bundle — whichever the
 * object actually carries, so one badge component serves both tables. The
 * yellow badge always says WHY: switched off, or on with nothing to sell.
 */
export function saleStatusOf(product) {
  const p = product && typeof product === 'object' ? product : {};
  // A bundle has `status` and no `app_enable`; a product the other way round.
  // Checking for the KEY (not for a truthy value) keeps app_enable: 0 from
  // falling through to a `status` that happens to be 1.
  const hasAppEnable = Object.prototype.hasOwnProperty.call(p, 'app_enable') &&
    p.app_enable !== null && p.app_enable !== undefined;
  const enabled = hasAppEnable ? isOn(p.app_enable) : isOn(p.status);

  // Stock half: products only, and only when the API answered. `sellable_qty`
  // absent ⇒ the switch alone decides (spec §1 edge case 4).
  const qty = hasAppEnable ? numOrNull(p.sellable_qty) : null;
  const hasStock = qty === null ? true : qty > 0;

  if (enabled && hasStock) {
    return {
      key: 'ready',
      type: 'success',
      labelKey: 'app_catalog.sale_status_ready',
      hintKey: ''
    };
  }
  return {
    key: 'not_ready',
    type: 'warning',
    labelKey: 'app_catalog.sale_status_not_ready',
    hintKey: enabled
      ? 'app_catalog.sale_status_hint_no_stock'
      : 'app_catalog.sale_status_hint_disabled'
  };
}

/**
 * hasActiveFlash(product, now) → boolean
 *
 * `now` is injectable so the window test is testable; it defaults to the clock.
 * A SKU counts when its flash price is > 0 AND the moment is inside whatever
 * part of the window it declares. NO window at all with a price set is treated
 * as running — that is exactly what the list endpoint's own predicate does, and
 * a bolt that disagreed with the list's `has_active_flash` would be worse than
 * either answer.
 */
export function hasActiveFlash(product, now) {
  const p = product && typeof product === 'object' ? product : {};
  if (isOn(p.has_active_flash)) return true;

  const at = toTime(now);
  const ts = at === null ? Date.now() : at;
  const skus = Array.isArray(p.skus) ? p.skus : [];
  for (let i = 0; i < skus.length; i++) {
    if (skuFlashActive(skus[i], ts)) return true;
  }
  // A bundle's flash lives on its slot options, not on SKUs.
  const slots = Array.isArray(p.slots) ? p.slots : [];
  for (let i = 0; i < slots.length; i++) {
    const options = slots[i] && Array.isArray(slots[i].options) ? slots[i].options : [];
    for (let j = 0; j < options.length; j++) {
      if (skuFlashActive(options[j], ts)) return true;
    }
  }
  return false;
}

/** One SKU / bundle option: is its flash price live at `ts`? */
function skuFlashActive(sku, ts) {
  if (!sku || typeof sku !== 'object') return false;
  const price = numOrNull(sku.flash_price);
  if (price === null || price <= 0) return false;
  const starts = toTime(sku.flash_starts_at);
  const ends = toTime(sku.flash_ends_at);
  if (starts !== null && ts < starts) return false;
  if (ends !== null && ts > ends) return false;
  return true;
}

/**
 * sellerPath(oldPath) → the same location under /seller.
 *
 * Query and hash ride along untouched (spec §1 edge case 1: an old bookmark
 * `/app-catalog/products/42/edit?tab=skus#sku-7` must land on the same tab).
 * Anything that is not an /app-catalog path — including a path that is already
 * rewritten — comes back unchanged, so this is safe to call twice.
 */
export function sellerPath(oldPath) {
  if (oldPath === null || oldPath === undefined) return '';
  const path = String(oldPath);
  if (path.indexOf(APP_CATALOG_PREFIX) !== 0) return path;
  const rest = path.slice(APP_CATALOG_PREFIX.length);
  // '/app-catalogue' must NOT match: the remainder has to start a new segment
  // (or be a query/hash, or be nothing at all).
  if (rest !== '' && rest[0] !== '/' && rest[0] !== '?' && rest[0] !== '#') return path;
  return SELLER_PREFIX + rest;
}

/**
 * skuRowsOf(productDetail) → rows for the expanded, headerless SKU sub-table.
 *
 * Field names are the API's on purpose (`sku_name`, `sku_code`, `quantity`):
 * the template reads them straight, and renaming them here would only add a
 * dictionary nobody can see from the page. `image` is resolved once, because
 * the detail endpoint answers with either `image_url` or an `images[]` array
 * depending on how the product was created.
 *
 * `fromWms` is the only derived flag: it drives the "จาก WMS" hint, and it is
 * stock_source === 'wms' and nothing else — a missing stock_source is manual
 * (same rule as @/utils/stockSource).
 *
 * `image` picks an ALREADY-ABSOLUTE candidate first (pickImageUrl): that is the
 * one the backend resolved and signed, and a bare object key sitting in an
 * earlier field must not shadow it — the page cannot sign a key, so a key wins
 * nothing but a broken thumbnail. The value is still passed through
 * cdnImageUrl() at render time. Signed-image spec §6.4.
 */
export function skuRowsOf(productDetail) {
  const p = productDetail && typeof productDetail === 'object' ? productDetail : {};
  const skus = Array.isArray(p.skus) ? p.skus : [];
  return skus.map((s, i) => {
    const sku = s && typeof s === 'object' ? s : {};
    const images = Array.isArray(sku.images) ? sku.images : [];
    const first = images[0] && typeof images[0] === 'object' ? images[0] : {};
    const qty = numOrNull(sku.quantity);
    return {
      key: sku.id ? 'sku-' + sku.id : 'idx-' + i,
      id: numOrNull(sku.id) || 0,
      image: pickImageUrl(sku.image_url, sku.image, first.cdn_url, first.url),
      sku_name: String(firstNonEmpty(sku.sku_name, sku.name_th, sku.name_en)),
      sku_code: String(firstNonEmpty(sku.sku_code, sku.code)),
      price: numOrNull(sku.price) === null ? 0 : Number(sku.price),
      quantity: qty === null ? 0 : qty,
      stock_source: String(firstNonEmpty(sku.stock_source)),
      fromWms: String(firstNonEmpty(sku.stock_source)) === 'wms',
      // Inline edit (2026-10-03 inline-edit spec §1 edge case 2): who owns the
      // DISCOUNTED price. `flash_source: 'shopee'` means shopeepromo writes
      // flash_* from the marketplace campaign, so the number the operator edits
      // here (the base price) is NOT what the storefront charges while the
      // campaign runs — the row says so instead of letting them find out.
      flash_source: String(firstNonEmpty(sku.flash_source)),
      flash_price: numOrNull(sku.flash_price) === null ? 0 : Number(sku.flash_price),
      // is_active is absent on an API that does not have the column; absent
      // means active, never inactive.
      is_active: sku.is_active === null || sku.is_active === undefined ? true : isOn(sku.is_active)
    };
  });
}

/**
 * bundleOptionRowsOf(bundleDetail) → one row per slot: the slot's label and the
 * display names of the things a customer may pick in it.
 *
 * NO price, NO stock, NO SKU (spec §5): a bundle slot is a choice, and the
 * numbers that matter live on the products the options point at — showing a
 * bundle's own price here would be a different, wrong number.
 */
export function bundleOptionRowsOf(bundleDetail) {
  const b = bundleDetail && typeof bundleDetail === 'object' ? bundleDetail : {};
  const slots = Array.isArray(b.slots) ? b.slots : [];
  return slots.map((s, i) => {
    const slot = s && typeof s === 'object' ? s : {};
    const options = Array.isArray(slot.options) ? slot.options : [];
    return {
      key: slot.id ? 'slot-' + slot.id : 'slot-idx-' + i,
      index: i + 1,
      label: String(firstNonEmpty(slot.label_th, slot.label_en)),
      label_th: String(firstNonEmpty(slot.label_th)),
      label_en: String(firstNonEmpty(slot.label_en)),
      options: options.map(o => {
        const opt = o && typeof o === 'object' ? o : {};
        return String(firstNonEmpty(
          opt.label_th,
          opt.label_en,
          opt.name_th,
          opt.name_en,
          opt.ref_name,
          opt.ref_id === null || opt.ref_id === undefined ? '' : opt.ref_id
        ));
      }).filter(name => name !== '')
    };
  });
}

/**
 * storefrontProductUrl(id) → the public PDP, or '' for a product with no id.
 *
 * www canonicalises `/p/{id}` to `/p/{slug}-{id}` with a 308 (mdf-www
 * src/app/(public)/p/[slugId]/page.tsx:69), so the bare id is a legal link and
 * the backoffice does not have to know the slug. '' keeps the ⋮ item from
 * opening about:blank on an unsaved row.
 */
export function storefrontProductUrl(id) {
  const n = numOrNull(id);
  if (n === null || n <= 0) return '';
  return storefrontOrigin() + '/p/' + Math.floor(n);
}

// --------------------------------------------------------------------------
// INLINE PRICE / QUANTITY EDIT
// (docs/specs/2026-10-03-seller-inline-price-qty-edit-design.md §4)
//
// The whole point of putting these here: the money rules must be testable
// WITHOUT mounting Products.vue, which this repo's jest cannot do.
// --------------------------------------------------------------------------

/** 1290 → "1,290.00". Always 2 dp: a price list with ragged decimals is unreadable. */
function money(n) {
  return Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/** The epsilon the BE compares prices with (`PriceEpsilon`, Member_price.go:51). */
export const PRICE_EPSILON = 0.005;

/**
 * priceRangeOf(skus) → { min, max, text }
 *
 *   text   "1,290.00~5,160.00", or ONE number when min === max, or '' when
 *          there is no SKU to speak about.
 *
 * SKUs with no usable price are skipped rather than counted as 0 — a product
 * with one priced SKU and one half-created one must not read "0.00~1,290.00".
 * The object carries a non-enumerable toString so a template may interpolate it
 * directly ({{ rowPriceRange(row) }}) without the page having to know the shape.
 */
export function priceRangeOf(skus) {
  const list = Array.isArray(skus) ? skus : [];
  let min = null;
  let max = null;
  for (let i = 0; i < list.length; i++) {
    const sku = list[i] && typeof list[i] === 'object' ? list[i] : {};
    const p = numOrNull(sku.price);
    if (p === null) continue;
    if (min === null || p < min) min = p;
    if (max === null || p > max) max = p;
  }
  if (min === null) return priceRange(0, 0, '');
  if (Math.abs(max - min) < PRICE_EPSILON) return priceRange(min, min, money(min));
  return priceRange(min, max, money(min) + '~' + money(max));
}

/** The { min, max, text } carrier, with text as its string form. */
function priceRange(min, max, text) {
  const out = { min: min, max: max, text: text };
  Object.defineProperty(out, 'toString', {
    value: function () { return this.text; },
    enumerable: false
  });
  return out;
}

/**
 * rowPriceRange(row, skus) → { min, max, text } for the COLLAPSED product row.
 *
 * Source order, best first:
 *   1. the expanded row's cached detail SKUs — they are what the operator just
 *      edited, so after a save the collapsed row must agree with them;
 *   2. `price_min` / `price_max` from the list endpoint (BE-2);
 *   3. the product's own `price`, for an API older than BE-2 — 0/0 from the
 *      range query means "could not compute", not "free" (spec §2 BE-2).
 */
export function rowPriceRange(row, skus) {
  const r = row && typeof row === 'object' ? row : {};
  const list = Array.isArray(skus) ? skus : null;
  if (list && list.length > 0) {
    const fromSkus = priceRangeOf(list);
    if (fromSkus.text !== '') return fromSkus;
  }
  const min = numOrNull(r.price_min);
  const max = numOrNull(r.price_max);
  if (min !== null && max !== null && (min > 0 || max > 0)) {
    if (Math.abs(max - min) < PRICE_EPSILON) return priceRange(min, min, money(min));
    return priceRange(min, max, money(min) + '~' + money(max));
  }
  const own = numOrNull(r.price);
  if (own === null) return priceRange(0, 0, '');
  return priceRange(own, own, money(own));
}

/**
 * canEditQty(skuRow) → may this SKU's quantity be typed at all?
 *
 * FALSE only for a WMS-owned bucket. Shared stock has been in WRITE mode since
 * 2026-10-03 11:33, so a hand-typed number on a WMS SKU is overwritten by the
 * next 20 s push: the input must not exist, and the BE answers 409
 * `quantity_wms_owned` to anyone who sends it anyway.
 *
 * Reads `fromWms` (what skuRowsOf derives) OR a raw `stock_source` straight off
 * the API, so the same function serves the table row and the response body.
 * An ABSENT stock_source is manual, never wms (same rule as @/utils/stockSource).
 */
export function canEditQty(skuRow) {
  const s = skuRow && typeof skuRow === 'object' ? skuRow : {};
  if (s.fromWms === true) return false;
  if (String(firstNonEmpty(s.stock_source)) === 'wms') return false;
  return true;
}

/**
 * allSkusFromWms(skuRows) → is there nothing on this product whose quantity a
 * human may type?
 *
 * Owner feedback, bo-stg 2026-10-03: pressing the จำนวนสินค้าพร้อมขาย ✎ on a
 * product whose SKUs are all warehouse-owned opened edit mode with every qty
 * cell read-only ("1227 จาก WMS"). The feature worked exactly as designed and
 * still read as broken, because the pencil is a promise. So the pencil is
 * withheld (and, when the answer is not yet known, the click explains itself)
 * whenever this is true.
 *
 * INACTIVE SKUs ARE IGNORED. `sellable_qty` is summed over live SKUs on the BE,
 * so a soft-retired manual SKU must not keep a pencil alive that can no longer
 * change the number the column shows.
 *
 * FALSE for an empty list / junk — "not known to be all-WMS". The pencil stays
 * visible on a row whose detail has never been fetched, which is the whole
 * reason the handler carries the same check: hiding a control on a guess is
 * worse than a click that answers in a toast.
 */
export function allSkusFromWms(skuRows) {
  const rows = Array.isArray(skuRows) ? skuRows : [];
  let live = 0;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i] && typeof rows[i] === 'object' ? rows[i] : {};
    // is_active absent ⇒ active (skuRowsOf's own rule, for an API with no column).
    if (row.is_active === false) continue;
    live++;
    if (canEditQty(row)) return false;
  }
  return live > 0;
}

/**
 * skuEditPayload(skuRow, draft) → {} | {price} | {quantity} | {price, quantity}
 *
 * The PATCH body. UNCHANGED FIELDS ARE OMITTED, which is the whole reason this
 * is a function and not an object literal in the template:
 *
 *   * a `price` that round-trips unchanged would still bump `updated_at` and
 *     re-enter the storefront sweep for nothing;
 *   * a `quantity` for a WMS SKU is refused with 409 and — because the handler
 *     is all-or-nothing — would take a perfectly good price edit down with it.
 *     So it is dropped here, before the request exists.
 *
 * Prices compare with the BE's epsilon (0.005); a draft that is empty, blank or
 * unparsable is "not typed", not zero. A non-integer quantity is dropped as
 * unparsable — the input is :precision="0", and the BE rejects it anyway.
 */
export function skuEditPayload(skuRow, draft) {
  const row = skuRow && typeof skuRow === 'object' ? skuRow : {};
  const d = draft && typeof draft === 'object' ? draft : {};
  const payload = {};

  const price = numOrNull(d.price);
  const wasPrice = numOrNull(row.price);
  if (price !== null && (wasPrice === null || Math.abs(price - wasPrice) >= PRICE_EPSILON)) {
    payload.price = price;
  }

  if (canEditQty(row)) {
    const qty = numOrNull(d.quantity);
    const wasQty = numOrNull(row.quantity);
    if (qty !== null && Math.floor(qty) === qty && qty !== wasQty) {
      payload.quantity = qty;
    }
  }
  return payload;
}

/** code → i18n key. The default is the generic failure, never a blank toast. */
const INLINE_ERROR_KEYS = {
  quantity_wms_owned: 'app_catalog.inline_qty_wms_refused',
  price_mode_fixed: 'app_catalog.inline_price_fixed_hint',
  price_invalid: 'app_catalog.inline_price_invalid',
  quantity_invalid: 'app_catalog.inline_qty_invalid',
  no_field: 'app_catalog.inline_save_failed'
};

/**
 * inlineEditErrorKey(err) → the i18n key for a rejected save.
 *
 * `err` may be an axios error, a response body, or the bare `code` string —
 * the caller should not have to dig, because digging wrong is a toast that says
 * "Could not save" about a 409 that has a precise reason (that the quantity
 * belongs to the warehouse). An HTTP status is used only as a last resort.
 */
export function inlineEditErrorKey(err) {
  const e = err && typeof err === 'object' ? err : {};
  const res = e.response && typeof e.response === 'object' ? e.response : {};
  const body = res.data && typeof res.data === 'object'
    ? res.data
    : (e.data && typeof e.data === 'object' ? e.data : e);
  const code = typeof err === 'string'
    ? err
    : String(firstNonEmpty(body.code, body.Code, e.code));
  if (code !== '' && Object.prototype.hasOwnProperty.call(INLINE_ERROR_KEYS, code)) {
    return INLINE_ERROR_KEYS[code];
  }
  return 'app_catalog.inline_save_failed';
}

// --------------------------------------------------------------------------
// Image preview + name → editor (owner 2026-10-15: "ถ้าคลิ๊กที่รูปให้แสดงรูปแบบ
// ใหญ่แบบ modal และเมื่อกดที่ชื่อสินค้าแล้วให้ไปหน้าแก้ไข").

/**
 * productEditPath(id) — the product editor route. The kebab's "แก้ไข", the
 * clickable name and goEdit() all take it from here so they cannot disagree.
 * "" for a row with no id (nothing to edit, render the name as text).
 */
export function productEditPath(id) {
  const raw = String(id === null || id === undefined ? '' : id).trim();
  if (raw === '' || raw === '0') return '';
  return SELLER_PREFIX + '/products/' + encodeURIComponent(raw) + '/edit';
}

/**
 * imagePreviewOf(row) → { src, caption } for the large-image lightbox, or null
 * when the row has no renderable picture (the placeholder is not clickable).
 *
 * `src` is the SAME URL as the thumbnail: CDN URLs are signed and must be used
 * byte for byte (see ./cdnImage), and the list row carries one URL only — the
 * full-size cover image, which the thumbnail merely scales down. There is no
 * size parameter to ask for.
 */
export function imagePreviewOf(row) {
  if (!row) return null;
  const src = cdnImageUrl(row.image_url);
  if (src === '') return null;
  const name = String(row.name_th || row.name_en || '').trim();
  return { src, caption: name };
}

/** isEscapeKey(event) — Esc across old (IE/Edge "Esc", keyCode 27) and new browsers. */
export function isEscapeKey(e) {
  return !!e && (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27);
}

export default {
  saleStatusOf,
  hasActiveFlash,
  sellerPath,
  skuRowsOf,
  bundleOptionRowsOf,
  storefrontOrigin,
  storefrontProductUrl,
  priceRangeOf,
  rowPriceRange,
  canEditQty,
  allSkusFromWms,
  skuEditPayload,
  inlineEditErrorKey,
  productEditPath,
  imagePreviewOf,
  isEscapeKey,
  PRICE_EPSILON
};
